import {
  warningConfig as tileWarningConfig,
  type TileWarningType,
} from 'pages/PhotoStyler/TileWarning/tileWarning.consts'
import { photoBookWarningConfig } from 'components/PhotoBook/photoBookWarning.consts'
import { EVENT_NAMES } from 'services/Analytics/Analytics'
import type {
  PhotoBookCartItemWarning,
  TileCartItemWarning,
} from 'stores/cartStore/CartItem'

export type WarningDisplay = {
  labelKey: string
  labelVars?: Record<string, unknown>
  ctaKey: string
}

const tileBagTapOverrides: Partial<Record<TileWarningType, string>> = {
  lowres: EVENT_NAMES.LOW_RESOLUTION_REVIEW_AND_REPLACE_TAPPED,
  crop: EVENT_NAMES.FACES_CROP_WARNING_REVIEW_TAPPED,
  'change-size': EVENT_NAMES.FACES_CROP_WARNING_REVIEW_TAPPED,
  'change-size-gallery-wall': EVENT_NAMES.FACES_CROP_WARNING_REVIEW_TAPPED,
  'art-tile-orientation': EVENT_NAMES.ART_ORIENTATION_WARNING_REVIEW_TAPPED,
}

export function getTileWarningAnalytics(warning: TileCartItemWarning) {
  const { analytics } = tileWarningConfig[warning.type]
  return {
    displayed: analytics.displayed,
    tapped: tileBagTapOverrides[warning.type] ?? analytics.tapped,
    dismissed: analytics.dismissed,
    props: {
      'Alert Type': warning.type,
      'Tile ID': warning.tileId,
    },
  }
}

export function getPhotoBookWarningAnalytics(
  warning: PhotoBookCartItemWarning
) {
  const { displayed, tapped, dismissed } =
    photoBookWarningConfig[warning.type].analytics
  return {
    displayed,
    tapped,
    dismissed,
    props: {
      'Alert Type': warning.type,
      ...(warning.missingPhotos != null && {
        'Missing Photos': warning.missingPhotos,
      }),
    },
  }
}

const tileCartOverrides: Partial<
  Record<TileWarningType, { label: string; cta: string }>
> = {
  crop: {
    label: 'order.faces_cropped_warning.cart_label',
    cta: 'order.faces_cropped_warning.cart_cta',
  },
  'change-size': {
    label: 'order.faces_cropped_warning.cart_label',
    cta: 'order.faces_cropped_warning.cart_cta',
  },
}

export function getTileWarningDisplay(
  warning: TileCartItemWarning
): WarningDisplay {
  const override = tileCartOverrides[warning.type]
  if (override) {
    return { labelKey: override.label, ctaKey: override.cta }
  }
  const { t } = tileWarningConfig[warning.type]
  return { labelKey: t.label, ctaKey: t.cta }
}

type CartItemWarning = TileCartItemWarning | PhotoBookCartItemWarning

const blockingCartWarningTypes = {
  lowres: false,
  crop: false,
  'change-size': false,
  'change-size-gallery-wall': false,
  'art-tile-orientation': false,
  'incomplete-collage': true,
  'ai-tile-generating': true,
  'not-enough-photos': true,
} as const satisfies Record<CartItemWarning['type'], boolean>

type BlockingCartWarningType = {
  [Type in keyof typeof blockingCartWarningTypes]: (typeof blockingCartWarningTypes)[Type] extends true
    ? Type
    : never
}[keyof typeof blockingCartWarningTypes]

type CartWarningOfType = { type: CartItemWarning['type'] }

type BlockingCartWarning<Warning extends CartWarningOfType> = {
  [Type in BlockingCartWarningType]: Warning & { type: Type }
}[BlockingCartWarningType]

export function isBlockingCartWarning<Warning extends CartWarningOfType>(
  warning: Warning
): warning is BlockingCartWarning<Warning> {
  return blockingCartWarningTypes[warning.type]
}

export function getPhotoBookWarningDisplay(
  warning: PhotoBookCartItemWarning
): WarningDisplay {
  const { t } = photoBookWarningConfig[warning.type]
  return {
    labelKey: t.label,
    ...(warning.missingPhotos != null && {
      labelVars: { numPhotos: warning.missingPhotos },
    }),
    ctaKey: t.cta,
  }
}
